import { useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, ShieldCheck, WarningCircle, Eye, EyeSlash, BellRinging, SpeakerHigh } from "@phosphor-icons/react";
import { enablePush, disablePush, pushSupported, playChime } from "../lib/push";

export default function AccountPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const forced = !!user?.must_change_password;
  const pwType = showPw ? "text" : "password";
  const [pushOn, setPushOn] = useState(user?.notification_prefs?.push !== false && typeof Notification !== "undefined" && Notification.permission === "granted");
  const [soundOn, setSoundOn] = useState(localStorage.getItem("prac_sound") !== "off");
  const [pushBusy, setPushBusy] = useState(false);

  const savePrefs = (push, sound) => api.put("/me/notification-prefs", { push, sound }).catch(() => {});

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (!pushOn) {
        await enablePush();
        setPushOn(true); savePrefs(true, soundOn);
        toast.success("Push notifications enabled on this device");
      } else {
        await disablePush();
        setPushOn(false); savePrefs(false, soundOn);
        toast.success("Push notifications turned off");
      }
    } catch (e) { toast.error(e.message || "Could not change push setting"); }
    finally { setPushBusy(false); }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("prac_sound", next ? "on" : "off");
    savePrefs(pushOn, next);
    if (next) playChime();
  };

  const testPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast[data.sent > 0 ? "success" : "error"](data.sent > 0 ? "Test notification sent — check your device" : "No active push subscription — enable push first");
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — stored securely (bcrypt-hashed)");
      setCurrent(""); setNext(""); setConfirm("");
      const u = await refresh();
      if (forced) navigate(u.role === "developer" ? "/developer-admin" : ["staff", "supervisor"].includes(u.role) ? "/briefing" : "/dashboard");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl space-y-8 rise-in" data-testid="account-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Profile Settings</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <LockKey size={36} weight="duotone" className="text-emerald-600" /> Security & Password
        </h1>
        <p className="text-slate-500 mt-3">Signed in as <span className="font-bold text-slate-700">{user?.name}</span> ({user?.email}). Passwords are bcrypt-hashed — nobody, including platform admins, can read them.</p>
      </div>

      {forced && (
        <div data-testid="force-password-banner" className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <WarningCircle size={24} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800">Set your private password to continue</p>
            <p className="text-sm text-amber-700 mt-1">Your account was created with a temporary password. Choose a new one only you know — the rest of the app unlocks right after.</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">{forced ? "Temporary password" : "Current password"}</label>
          <input data-testid="current-password-input" type={pwType} required value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="mt-2 w-full h-14 px-5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">New password (8+ characters)</label>
          <input data-testid="new-password-input" type={pwType} required value={next}
            onChange={(e) => setNext(e.target.value)}
            className="mt-2 w-full h-14 px-5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Confirm new password</label>
          <input data-testid="confirm-password-input" type={pwType} required value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-2 w-full h-14 px-5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <button type="button" data-testid="account-password-toggle" onClick={() => setShowPw(!showPw)}
          className="text-xs font-bold text-slate-500 flex items-center gap-1.5 tap-press hover:text-slate-900">
          {showPw ? <EyeSlash size={16} /> : <Eye size={16} />} {showPw ? "Hide passwords" : "Show passwords"}
        </button>
        <button data-testid="change-password-submit" type="submit" disabled={busy}
          className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold flex items-center justify-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
          <ShieldCheck size={20} weight="duotone" /> {busy ? "Updating…" : forced ? "Set my private password" : "Update password"}
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="notification-settings">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2"><BellRinging size={24} weight="duotone" /> Notification Settings</h2>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-bold text-sm">Push notifications</p>
            <p className="text-xs text-slate-500 mt-1">OS-level popup banners on this device — even when the app is closed. {!pushSupported() && "(Not supported on this browser)"}</p>
          </div>
          <button data-testid="push-toggle" onClick={togglePush} disabled={pushBusy || !pushSupported()}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-40 ${pushOn ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${pushOn ? "left-6" : "left-1"}`} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-bold text-sm flex items-center gap-1.5"><SpeakerHigh size={16} /> Sound alerts</p>
            <p className="text-xs text-slate-500 mt-1">Play a chime when new alerts arrive while you're in the app.</p>
          </div>
          <button data-testid="sound-toggle" onClick={toggleSound}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${soundOn ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${soundOn ? "left-6" : "left-1"}`} />
          </button>
        </div>
        <button data-testid="push-test-button" onClick={testPush}
          className="h-11 px-5 rounded-full border border-slate-200 text-xs font-bold text-slate-600 tap-press hover:border-slate-900">
          Send test notification
        </button>
      </div>

      <p className="text-xs text-slate-400">Accounts lock for 15 minutes after 5 failed sign-in attempts. Contact your administrator if you're locked out.</p>
    </div>
  );
}
